import { weekInfoPerCulture } from "client/DateAndTime/culture";
import { ensureNotNil } from "client/Misc/utils";
import { Settings as luxonSettings} from "luxon";
import { strings } from "../../client/localizations";

let currentCulture: string = null!;
let parentCulture: string | null = null;

function initialize() {
	// if the currentCulture is already set it was initialized before
	if (currentCulture) {
		return;
	}

	currentCulture = document.querySelector("html")?.lang || "en";

	determineParentCulture();

	luxonSettings.defaultLocale = currentCulture;
	luxonSettings.defaultWeekSettings = weekInfoPerCulture[currentCulture]
		?? ensureNotNil(weekInfoPerCulture["en"]);
}

function determineParentCulture() {
	const indexSplitChar: number = currentCulture.indexOf("-"); // e.g. en-GB
	parentCulture = (indexSplitChar === -1) ? null : currentCulture.substring(0, indexSplitChar);
}

function getCurrentCulture(): string {
	return currentCulture;
}

function formatDuration(durationInSeconds: number): string {
	let duration: number = Math.floor(durationInSeconds);
	if (duration < 60) {
		return `${duration} ${localize("_SecondsUnit")}`;
	}

	duration = Math.floor(duration / 60);

	if (duration < 60) {
		return `${duration} ${localize("_MinutesUnit")}`;
	}

	duration = duration / 60;

	return `${duration.toFixed(1)} ${localize("_HoursUnit")}`;
}

function localize(key: LocalizationKey): string {
	// If no localization is defined
	if (!strings) {
		return key;
	}
	// First we look at the culture which is selected
	if (strings[currentCulture] && (strings[currentCulture])[key]) {
		return (strings[currentCulture])[key];
	}

	if (parentCulture && strings[parentCulture] && (strings[parentCulture])[key]) {
		return (strings[parentCulture])[key];
	}

	// If it was not found we fallback to default
	if (strings["standard"] && strings["standard"][key]) {
		return strings["standard"][key];
	}
	// If still nothing was found we just return the key
	return key;
}

export type CultureResolvableMap<TValue> = { standard: TValue; } & Record<string, TValue>;

export function resolveForCurrentCulture<TValue>(
	map: CultureResolvableMap<TValue>
): TValue {
	if (currentCulture in map) {
		return map[currentCulture];
	}

	if (parentCulture && parentCulture in map) {
		return map[parentCulture];
	}

	return map.standard;
}

function localizeWithHtmlLineFeeds(key: LocalizationKey): string {
	return localize(key).replace(/(?:\r\n|\r|\n)/g, "<br>");
}

// Make sure culture is initialized as early as possible when requested
initialize();

export {
	getCurrentCulture,
	localize,
	localizeWithHtmlLineFeeds,
	formatDuration
};
